import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { Evaluation } from "@/types/office/Evaluation";
import { OrderDetail, SlimOrder } from "@/types/order/Order";
import { OrderSearchQuery } from "@/types/order/OrderSearchQuery";
import { OrderSortKey } from "@/types/order/OrderSortKey";
import { orderService } from "@/app/services/OrderService";
import { addMinutes, now, parseISO } from "@/utils/date";

// localStorageにorderが格納されているか確認
const ls = localStorage.getItem("vuex");
const localStorageOrder = ls !== null ? JSON.parse(ls).order : undefined;

const LIMITED_ORDER_CACHE_TTL = 10;

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "order",
  // ローカルストレージにorderが存在している場合のみ有効化
  preserveState: localStorageOrder !== undefined
})
class OrderModule extends VuexModule {
  alreadyGeoLocationModalShown = false;
  orderDetail: OrderDetail | null = null;
  orderSearchQuery: OrderSearchQuery | null = null;
  orderSortKey: OrderSortKey | null = null;

  // 古いstoreの場合にはundefinedになる
  orderList?: SlimOrder[] = [];
  individualOfferOrderList: SlimOrder[] = [];
  evaluations?: Record<number, Evaluation> = {};

  watchLaterOrderList: { id: number; date: string; isHiring?: boolean }[] = [];

  // 当日・翌日は応募可能である旨のトーストを前回表示した日時
  shownNotLimitApplicationToastTime: number | null = null;

  // アカウント認証送信が必要な旨のトーストを前回表示した日時
  shownUnsendAccountVerificationToastTime: number | null = null;

  // 限定公開案件のキャッシュ
  limitedOrderListCache?: SlimOrder[] = [];
  limitedOrderListCacheExpiredAt = 0;
  limitedOrderListCountCache = 0;
  limitedOrderListCountCacheExpiredAt = 0;
  isAllLimitedOrderLoaded = false;
  showPageOfLimitedOrderList = 1;
  // 薬剤師の場合、検索条件に一度だけ「サービス種別：薬局」をフィルタに設定するが、その設定をしたかどうか
  hasUsedOneTimePharmacistRegistrationFilter = false;

  // 事業所詳細の案件一覧で募集中の案件のみ表示するかどうか
  hiringOnly = false;

  get existOrderDetail() {
    return this.orderDetail !== null;
  }

  @Mutation
  private SET_ORDER_DETAIL(orderDetail: OrderDetail | null) {
    this.orderDetail = orderDetail;
  }

  @Mutation
  private SET_ORDER_LIST(orderList: SlimOrder[]) {
    this.orderList = orderList;
  }

  @Mutation
  private SET_INDIVIDUAL_OFFER_ORDER_LIST(orderList: SlimOrder[]) {
    this.individualOfferOrderList = orderList;
  }

  @Mutation
  private SET_ORDER_SORT_KEY(orderSortKey: OrderSortKey) {
    this.orderSortKey = orderSortKey;
  }

  @Mutation
  private CLEAR_ORDER_SORT_KEY() {
    this.orderSortKey = null;
  }

  @Mutation
  private SET_ORDER_SEARCH_QUERY(orderSearchQuery: OrderSearchQuery) {
    this.orderSearchQuery = orderSearchQuery;
  }

  @Mutation
  private CLEAR_ORDER_SEARCH_QUERY() {
    this.orderSearchQuery = {};
  }

  @Mutation
  private CLEAR_QUALIFICATIONS_FROM_ORDER_SEARCH_QUERY() {
    if (this.orderSearchQuery && this.orderSearchQuery.qualifications) {
      this.orderSearchQuery.qualifications = null;
    }
  }

  @Mutation
  private SET_ALREADY_GEO_LOCATION_MODAL_SHOWN(shown: boolean) {
    this.alreadyGeoLocationModalShown = shown;
  }

  @Mutation
  private SET_SHOWN_NOT_LIMIT_APPLICATION_TOAST_TIME(time: number) {
    this.shownNotLimitApplicationToastTime = time;
  }

  @Mutation
  private SET_SHOWN_UNSEND_ACCOUNT_VERIFICATION_TOAST_TIME(time: number) {
    this.shownUnsendAccountVerificationToastTime = time;
  }

  @Mutation
  private SET_EVALUATIONS(evaluations: Record<number, Evaluation>) {
    this.evaluations = evaluations;
  }

  @Mutation
  private SET_LIMITED_ORDER_LIST_CACHE(orderList: SlimOrder[]) {
    this.limitedOrderListCache = orderList;
  }

  @Mutation
  private SET_LIMITED_ORDER_LIST_CACHE_EXPIRED_AT(expiredAt: number) {
    this.limitedOrderListCacheExpiredAt = expiredAt;
  }

  @Mutation
  private SET_LIMITED_ORDER_LIST_COUNT_CACHE(count: number) {
    this.limitedOrderListCountCache = count;
  }

  @Mutation
  private SET_LIMITED_ORDER_LIST_COUNT_CACHE_EXPIRED_AT(expiredAt: number) {
    this.limitedOrderListCountCacheExpiredAt = expiredAt;
  }

  @Mutation
  private SET_IS_ALL_LIMITED_ORDER_LOADED(isAllLimitedOrderLoaded: boolean) {
    this.isAllLimitedOrderLoaded = isAllLimitedOrderLoaded;
  }

  @Mutation
  private SET_SHOW_PAGE_OF_LIMITED_ORDER_LIST(page: number) {
    this.showPageOfLimitedOrderList = page;
  }

  @Mutation
  private SET_HAS_USED_ONE_TIME_PHARMACIST_REGISTRATION_FILTER() {
    this.hasUsedOneTimePharmacistRegistrationFilter = true;
  }

  @Mutation
  private SET_HIRING_ONLY(hiringOnly: boolean) {
    this.hiringOnly = hiringOnly;
  }

  @Action
  fetchOrderDetail(params: {
    orderId: number;
    ignoreWorkerService: boolean;
    shareKey?: string;
    transitionSource?: string;
  }) {
    // TODO: ignoreWorkerService が機能していないので、あるべき姿を整理して api 側ともども修正する
    // 以下経緯
    // ignoreWorkerService が true の場合、ユーザーの業務カテゴリに関係なく案件情報を取得することを想定している（お仕事詳細リンクなどで利用）
    // ただ、現状 api 側の比較条件が誤っている (クエリパラメータに付与するため文字列として比較する必要があるが真偽値で比較してしまっている) ことから機能しておらず、ignoreWorkerService を指定することで常に上記仕様で動作している
    // https://gitlab.com/caitech/caisuke-api/-/blob/master/apps/atma/orders/access_policy.py#L68
    // また、ignoreWorkerService はもともと router.params を使って値の受け渡しをしていたが、vue-router v4 ではルート定義上存在しない params を指定することはできないため、別の手段で値をやりとりする必要がある
    // https://github.com/vuejs/router/blob/main/packages/router/CHANGELOG.md#414-2022-08-22
    return orderService
      .fetchOrder(
        params.orderId,
        params.ignoreWorkerService,
        params.shareKey,
        params.transitionSource
      )
      .then(orderDetail => {
        this.SET_ORDER_DETAIL(orderDetail);
      })
      .catch(e => {
        this.SET_ORDER_DETAIL(null);
        throw e;
      });
  }

  @Action
  setSearchQuery(orderSearchQuery: OrderSearchQuery) {
    this.SET_ORDER_SEARCH_QUERY(orderSearchQuery);
  }

  @Action
  setOrderList(orderList: SlimOrder[]) {
    this.SET_ORDER_LIST(orderList);
  }

  @Action
  addOrderList(orderList: SlimOrder[]) {
    this.SET_ORDER_LIST([...(this.orderList || []), ...orderList]);
  }

  @Action
  addIndividualOfferOrderList(orderList: SlimOrder[]) {
    this.SET_INDIVIDUAL_OFFER_ORDER_LIST([
      ...(this.individualOfferOrderList || []),
      ...orderList
    ]);
  }

  @Action
  setIndividualOfferOrderList(orderList: SlimOrder[]) {
    this.SET_INDIVIDUAL_OFFER_ORDER_LIST(orderList);
  }

  @Action
  clearIndividualOfferOrderList() {
    this.SET_INDIVIDUAL_OFFER_ORDER_LIST([]);
  }

  @Action
  clearOrderList() {
    this.SET_ORDER_LIST([]);
  }

  @Action
  clearSearchQuery() {
    this.CLEAR_ORDER_SEARCH_QUERY();
  }

  @Action
  setOrderSortKey(orderSortKey: OrderSortKey) {
    this.SET_ORDER_SORT_KEY(orderSortKey);
  }

  @Action
  clearOrderSortKey() {
    this.CLEAR_ORDER_SORT_KEY();
  }

  @Action
  clearQualificationsFromSearchQuery() {
    this.CLEAR_QUALIFICATIONS_FROM_ORDER_SEARCH_QUERY();
  }

  @Action
  setAlreadyGeoLocationModalShown(shown: boolean) {
    this.SET_ALREADY_GEO_LOCATION_MODAL_SHOWN(shown);
  }

  @Mutation
  private SET_WATCH_LATER_ORDER_LIST(
    orderList: { id: number; date: string; isHiring?: boolean }[]
  ) {
    this.watchLaterOrderList = orderList;
  }

  @Action
  setUpWatchLaterOrderList() {
    if (!this.watchLaterOrderList) {
      this.SET_WATCH_LATER_ORDER_LIST([]);
    }
  }

  @Action
  addToWatchLaterOrderList(order: {
    id: number;
    date: string;
    isHiring?: boolean;
  }) {
    this.SET_WATCH_LATER_ORDER_LIST([
      ...this.watchLaterOrderList,
      { id: order.id, date: order.date, isHiring: order.isHiring }
    ]);
  }

  @Action
  filterHiringOrdersFromWatchLaterOrderList(ids: number[]) {
    this.SET_WATCH_LATER_ORDER_LIST(
      this.watchLaterOrderList
        .map(order => {
          if (ids.includes(order.id)) {
            return { ...order, isHiring: true };
          }
          return { ...order, isHiring: false };
        })
        .filter(order => order.isHiring)
    );
  }

  @Action
  removeFromWatchLaterOrderList(orderId: number) {
    this.SET_WATCH_LATER_ORDER_LIST(
      this.watchLaterOrderList.filter(order => order.id !== orderId)
    );
  }

  @Action
  removeExpiredOrdersFromWatchLaterOrderList(dateNow: number) {
    this.SET_WATCH_LATER_ORDER_LIST(
      this.watchLaterOrderList.filter(order => parseISO(order.date) >= dateNow)
    );
  }

  @Action
  setShownNotLimitApplicationToastTime(time: number) {
    this.SET_SHOWN_NOT_LIMIT_APPLICATION_TOAST_TIME(time);
  }

  @Action
  setShownUnsendAccountVerificationToastTime(time: number) {
    this.SET_SHOWN_UNSEND_ACCOUNT_VERIFICATION_TOAST_TIME(time);
  }

  @Action
  clearEvaluations() {
    this.SET_EVALUATIONS([]);
  }

  @Action
  addEvaluations(evaluations: Evaluation[]) {
    this.SET_EVALUATIONS({
      ...this.evaluations,
      ...Object.fromEntries(evaluations.map(e => [e.id, e]))
    });
  }

  @Action
  addLimitedOrderListCache(orderList: SlimOrder[]) {
    this.SET_LIMITED_ORDER_LIST_CACHE([
      ...(this.limitedOrderListCache || []),
      ...orderList
    ]);
    // 10分間キャッシュする
    this.SET_LIMITED_ORDER_LIST_CACHE_EXPIRED_AT(
      addMinutes(now(), LIMITED_ORDER_CACHE_TTL)
    );
  }

  @Action
  setLimitedOrderListCache(orderList: SlimOrder[]) {
    this.SET_LIMITED_ORDER_LIST_CACHE(orderList);
    // 10分間キャッシュする
    this.SET_LIMITED_ORDER_LIST_CACHE_EXPIRED_AT(
      addMinutes(now(), LIMITED_ORDER_CACHE_TTL)
    );
  }

  @Action
  clearLimitedOrderListCache() {
    this.SET_LIMITED_ORDER_LIST_CACHE([]);
    this.SET_IS_ALL_LIMITED_ORDER_LOADED(false);
    this.SET_SHOW_PAGE_OF_LIMITED_ORDER_LIST(1);
    this.SET_LIMITED_ORDER_LIST_CACHE_EXPIRED_AT(0);
  }

  @Action
  setLimitedOrderListCountCache(count: number) {
    this.SET_LIMITED_ORDER_LIST_COUNT_CACHE(count);
    // 10分間キャッシュする
    this.SET_LIMITED_ORDER_LIST_COUNT_CACHE_EXPIRED_AT(
      addMinutes(now(), LIMITED_ORDER_CACHE_TTL)
    );
  }

  @Action
  clearLimitedOrderListCountCache() {
    this.SET_LIMITED_ORDER_LIST_COUNT_CACHE(0);
    this.SET_LIMITED_ORDER_LIST_COUNT_CACHE_EXPIRED_AT(0);
  }

  @Action
  setIsAllLimitedOrderLoaded(isAllLimitedOrderLoaded: boolean) {
    this.SET_IS_ALL_LIMITED_ORDER_LOADED(isAllLimitedOrderLoaded);
  }

  @Action
  setShowPageOfLimitedOrderList(page: number) {
    this.SET_SHOW_PAGE_OF_LIMITED_ORDER_LIST(page);
  }

  @Action
  setHasUsedOneTimePharmacistRegistrationFilter() {
    this.SET_HAS_USED_ONE_TIME_PHARMACIST_REGISTRATION_FILTER();
  }

  @Action
  setHiringOnly(hiringOnly: boolean) {
    this.SET_HIRING_ONLY(hiringOnly);
  }
}

export const orderStore = getModule(OrderModule);
